/**
 * Contributions: the append-only audit table, filtered server-side to what
 * this role may see.
 *
 * The table renders exactly what the feed returns. There is no client-side
 * aggregation and no "load everything" — the server pages by event id, and
 * this view shows the most recent window with a mine/everyone toggle.
 */

import { useCallback, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { fetchContributions } from '../api.js';
import { EventTime, TaskTypeFilter, useWorkspaceCopy } from '../components.js';
import { useSession } from '../session.js';
import { Button, EmptyState } from '../ui.js';
import { FailurePanel, LoadingPanel, PageTitle, useAsync } from './common.js';

export function ContributionsPage(): ReactNode {
  const copy = useWorkspaceCopy();
  const { session } = useSession();
  const [mine, setMine] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const { state, reload } = useAsync(
    useCallback(
      () =>
        fetchContributions({
          mine,
          ...(typeFilter === null ? {} : { type: typeFilter }),
        }),
      [mine, typeFilter],
    ),
  );

  const visibleTypes =
    session.status === 'authenticated' ? session.profile.visibleTaskTypes : [];

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <PageTitle>{copy.contributions.title}</PageTitle>
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-md)',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <TaskTypeFilter
            visibleTypes={visibleTypes}
            value={typeFilter}
            onChange={setTypeFilter}
          />
          <div
            role="group"
            aria-label={copy.contributions.title}
            style={{ display: 'flex', gap: 6 }}
          >
            <Button
              size="sm"
              variant={mine ? 'quiet' : 'outline'}
              aria-pressed={!mine}
              onClick={() => {
                setMine(false);
              }}
            >
              {copy.contributions.all}
            </Button>
            <Button
              size="sm"
              variant={mine ? 'outline' : 'quiet'}
              aria-pressed={mine}
              onClick={() => {
                setMine(true);
              }}
            >
              {copy.contributions.mine}
            </Button>
          </div>
        </div>
      </div>

      {state.status === 'loading' ? (
        <LoadingPanel />
      ) : state.status === 'failed' ? (
        <FailurePanel failure={state.failure} onRetry={reload} />
      ) : state.value.events.length === 0 ? (
        <EmptyState title={copy.contributions.empty} />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="mj-table">
            <thead>
              <tr>
                <th scope="col">{copy.contributions.columns.when}</th>
                <th scope="col">{copy.contributions.columns.contributor}</th>
                <th scope="col">{copy.contributions.columns.task}</th>
                <th scope="col">{copy.contributions.columns.event}</th>
                <th scope="col">{copy.contributions.columns.note}</th>
              </tr>
            </thead>
            <tbody>
              {state.value.events.map((event) => (
                <tr key={event.id}>
                  <td>
                    <EventTime iso={event.occurredAt} />
                  </td>
                  <td>{event.actor.displayName}</td>
                  <td>
                    <Link
                      to={`/tasks/${event.taskId}`}
                      style={{ color: 'var(--text-link)' }}
                    >
                      <span
                        className="mj-ltr"
                        dir="ltr"
                        style={{ font: 'var(--type-body-tabular)' }}
                      >
                        {event.taskReference}
                      </span>{' '}
                      {event.taskTitle}
                    </Link>
                  </td>
                  <td>{copy.events[event.kind] ?? event.kind}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>
                    {event.note ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
